import React, { useEffect, useState } from 'react'
import { Button } from 'semantic-ui-react'
import styled from 'styled-components'
import { deleteDeal, moveDealStage, reopenDeal } from 'utility/services/crm'
import { WON_STAGE } from 'utility/crm/deal-board'
import { formatMoney, formatShortDate, relativeDays } from 'utility/crm/dates'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmToast, useDeal, useDealActivities, useIsCrmAdmin } from 'hooks/crm'
import { CrmDrawer, ConfirmDialog } from './CrmOverlay'
import { AsyncState, Muted, Pill, Row, StagePill } from './ui'

const Grid = styled.dl`
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 8px 12px;
    margin: 0 0 16px;
    dt {
        opacity: 0.7;
    }
    dd {
        margin: 0;
    }
`

const Entry = styled.div`
    padding: 10px 0;
    border-bottom: 1px solid rgba(128, 128, 128, 0.25);
    .head {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        font-weight: 600;
    }
    .body {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        margin: 4px 0;
    }
`

/**
 * Deal drawer: details, "Move to stage" (the keyboard alternative to drag and drop), proposal,
 * won/lost/reopen, edit and delete (admin), and the deal's activity.
 * @param {{
 *   dealId: number|null,
 *   onClose: () => void,
 *   onEdit: (deal: import('utility/crm/types').CrmDeal) => void,
 *   onProposal: (deal: import('utility/crm/types').CrmDeal) => void,
 *   onWon: (deal: import('utility/crm/types').CrmDeal) => void,
 *   onLost: (deal: import('utility/crm/types').CrmDeal) => void,
 *   onViewProposal: (deal: import('utility/crm/types').CrmDeal) => void,
 *   onOpenContact: (contactId: number) => void,
 * }} props
 */
export const DealDrawer = ({ dealId, onClose, onEdit, onProposal, onWon, onLost, onViewProposal, onOpenContact }) => {
    const isAdmin = useIsCrmAdmin()
    const { lookups } = useCrmLookups()
    const { successToast, showError } = useCrmToast()
    const dealQuery = useDeal(dealId)
    const activities = useDealActivities(dealId)
    const deal = dealQuery.data && dealQuery.data.DealId === dealId ? dealQuery.data : null
    const [stage, setStage] = useState('')
    const [busy, setBusy] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)

    useEffect(() => {
        if (deal) setStage(deal.Stage)
    }, [deal?.DealId, deal?.Stage]) // eslint-disable-line

    const run = async (action, message) => {
        setBusy(true)
        try {
            await action()
            if (message) successToast(message)
            invalidateCrm(...ALL_CRM_SCOPES)
        } catch (err) {
            showError(err)
            invalidateCrm('deals', 'forecast') // 409s: show the real state
        } finally {
            setBusy(false)
        }
    }

    const move = () => {
        if (stage === WON_STAGE) {
            // Never send Won to the Stage endpoint: it starts the customer handoff instead.
            onWon(deal)
            return
        }
        run(() => moveDealStage(deal.DealId, stage), `Moved to ${stage}`)
    }

    const remove = async () => {
        setBusy(true)
        try {
            await deleteDeal(deal.DealId)
            successToast('Deal deleted')
            invalidateCrm(...ALL_CRM_SCOPES)
            setConfirmDelete(false)
            onClose()
        } catch (err) {
            showError(err)
            setConfirmDelete(false)
        } finally {
            setBusy(false)
        }
    }

    const isOpen = deal?.Status === 'open'
    const stageNames = (lookups?.Stages || []).map(s => s.Name)

    return (
        <CrmDrawer open={Boolean(dealId)} onClose={onClose} title={deal ? `${deal.AccountName} · ${deal.Name}` : 'Deal'} width={560}>
            <AsyncState loading={dealQuery.loading} error={dealQuery.error} hasData={Boolean(deal)} onRetry={dealQuery.refetch}>
                {deal ? (
                    <>
                        <Row $gap={8} $wrap style={{ marginBottom: 12 }}>
                            <StagePill stage={deal.Status === 'lost' ? 'Lost' : deal.Stage} />
                            <Pill>{deal.Pipeline}</Pill>
                            {deal.Status === 'open' && deal.IsStuck ? <Pill $tone="amber">{deal.DaysInStage} days in stage</Pill> : null}
                            {deal.Status === 'open' && deal.IsPastClose ? <Pill $tone="red">Close date passed</Pill> : null}
                        </Row>

                        <Grid>
                            <dt>Amount</dt>
                            <dd>
                                <strong>{formatMoney(deal.Amount)}</strong> · weighted {formatMoney(deal.Weighted)}
                            </dd>
                            <dt>Close date</dt>
                            <dd>{deal.CloseDate ? `${formatShortDate(deal.CloseDate)} (${relativeDays(deal.CloseDate)})` : '–'}</dd>
                            <dt>Contact</dt>
                            <dd>
                                {deal.ContactId ? (
                                    <button type="button" onClick={() => onOpenContact(deal.ContactId)} style={{ background: 'none', border: 0, color: '#2563eb', cursor: 'pointer', padding: 0 }}>
                                        {deal.ContactName}
                                    </button>
                                ) : (
                                    '–'
                                )}
                            </dd>
                            <dt>Owner</dt>
                            <dd>{deal.OwnerName || '–'}</dd>
                            {deal.Status === 'lost' ? (
                                <>
                                    <dt>Lost reason</dt>
                                    <dd>{deal.LostReason || '–'}</dd>
                                </>
                            ) : null}
                            {deal.Proposal ? (
                                <>
                                    <dt>Proposal</dt>
                                    <dd>
                                        {deal.Proposal.Summary}{' '}
                                        <button type="button" onClick={() => onViewProposal(deal)} style={{ background: 'none', border: 0, color: '#2563eb', cursor: 'pointer', padding: 0 }}>
                                            View
                                        </button>
                                    </dd>
                                </>
                            ) : null}
                        </Grid>

                        {isOpen ? (
                            <div style={{ marginBottom: 16 }}>
                                <label htmlFor="crm-move-stage" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                                    Move to stage
                                </label>
                                <Row $gap={8}>
                                    <select id="crm-move-stage" value={stage} onChange={event => setStage(event.target.value)} style={{ flex: 1, padding: 8 }} disabled={busy}>
                                        {stageNames.map(name => (
                                            <option key={name} value={name}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                    <Button type="button" onClick={move} disabled={busy || stage === deal.Stage} loading={busy}>
                                        Move
                                    </Button>
                                </Row>
                            </div>
                        ) : null}

                        <Row $gap={8} $wrap style={{ marginBottom: 20 }}>
                            {isOpen ? (
                                <>
                                    <Button type="button" onClick={() => onProposal(deal)} disabled={busy}>
                                        Build proposal
                                    </Button>
                                    <Button type="button" primary onClick={() => onWon(deal)} disabled={busy}>
                                        Mark won
                                    </Button>
                                    <Button type="button" color="red" basic onClick={() => onLost(deal)} disabled={busy}>
                                        Mark lost
                                    </Button>
                                    <Button type="button" onClick={() => onEdit(deal)} disabled={busy}>
                                        Edit deal
                                    </Button>
                                </>
                            ) : null}
                            {deal.Status === 'lost' ? (
                                <Button type="button" primary onClick={() => run(() => reopenDeal(deal.DealId), 'Deal reopened')} loading={busy} disabled={busy}>
                                    Reopen deal
                                </Button>
                            ) : null}
                            {deal.Status === 'won' ? <Muted>Won deals are read-only.</Muted> : null}
                            {isAdmin ? (
                                <Button type="button" basic negative onClick={() => setConfirmDelete(true)} disabled={busy}>
                                    Delete
                                </Button>
                            ) : null}
                        </Row>

                        <h4 style={{ margin: '0 0 6px' }}>Deal activity</h4>
                        <AsyncState loading={activities.loading} error={activities.error} hasData={Boolean(activities.data)} empty={Boolean(activities.data) && activities.data.length === 0} emptyText="No activity on this deal yet." onRetry={activities.refetch}>
                            {(activities.data || []).map(a => (
                                <Entry key={a.ActivityId}>
                                    <div className="head">
                                        <span>{a.Title}</span>
                                        <Muted>
                                            {formatShortDate(a.Date)} · {relativeDays(a.Date)}
                                        </Muted>
                                    </div>
                                    {a.Body ? <div className="body">{a.Body}</div> : null}
                                    <Muted>{[a.Type, a.ContactName, a.OwnerName].filter(Boolean).join(' · ')}</Muted>
                                </Entry>
                            ))}
                        </AsyncState>

                        <ConfirmDialog
                            open={confirmDelete}
                            danger
                            title="Delete deal"
                            message={`Delete ${deal.Name}? This cannot be undone.`}
                            confirmLabel="Delete"
                            loading={busy}
                            onConfirm={remove}
                            onClose={() => setConfirmDelete(false)}
                        />
                    </>
                ) : null}
            </AsyncState>
        </CrmDrawer>
    )
}
